import './BufferTable.scss';

import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import { pacingTypes } from '../../../../../../../../../../configs';
import bem from '../../../../../../../../../../utils/bem';
import {
  getDraxNullBillableThirdPartyBufferTooltip,
  shouldDisableBufferOptIn,
} from '../../../../../../../../../../utils/lineItem';
import type { LineItemEntity } from '../../../../../../types';
import MultipleEditDrawerContentTable from '../../../../../MultipleEditDrawerContentTable';
import { INTERNATIONAL_LINE_TOOLTIP } from '../constants';
import { useBufferTable } from './hooks/useBufferTable';

const [block, element] = bem('multiple-edit-buffer-table');

type BufferTableProps = Readonly<{
  isExpanded: boolean;
  entities: LineItemEntity[];
  isAutomated?: boolean;
}>;

export type BufferTableRef = {
  applyChangesToFormik: () => void;
};

const BufferTable = forwardRef<BufferTableRef, BufferTableProps>(
  ({ isExpanded, entities, isAutomated }, ref): JSX.Element => {
    const tableClassName = `${block()} ${isExpanded ? block('expanded') : ''}`;
    const { columns, data, initialState, listenSelectedRows, applyChangesToFormik } = useBufferTable(
      element,
      entities,
      isAutomated
    );

    useImperativeHandle(ref, () => ({
      applyChangesToFormik,
    }));

    const shouldDisableRowToggle = useCallback(
      (row: LineItemEntity): boolean =>
        shouldDisableBufferOptIn({
          pacingType: row.pacingType,
          ssp: row.ssp,
          billableThirdParty: row.billableThirdParty,
        }),
      []
    );

    const getToggleTooltip = useCallback(
      (row: LineItemEntity): string | undefined => {
        if (!shouldDisableRowToggle(row)) {
          return undefined;
        }

        const isSOV = row.pacingType?.key === pacingTypes.SOV.key;
        if (isSOV) {
          return INTERNATIONAL_LINE_TOOLTIP;
        }

        return getDraxNullBillableThirdPartyBufferTooltip({
          ssp: row.ssp,
          billableThirdParty: row.billableThirdParty,
        });
      },
      [shouldDisableRowToggle]
    );

    const disableAllToggles = useMemo(() => entities.some((entity) => shouldDisableRowToggle(entity)), [
      entities,
      shouldDisableRowToggle,
    ]);

    return (
      <div className={tableClassName}>
        <MultipleEditDrawerContentTable
          isSortable
          addCheckbox={false}
          addToggle={true}
          disableAllToggles={disableAllToggles}
          shouldDisableRowToggle={shouldDisableRowToggle}
          getToggleTooltip={getToggleTooltip}
          toggleColumnHeaderText={'BUFFER OPT-IN'}
          data={data}
          columns={columns}
          initialState={initialState}
          listenSelectedRows={listenSelectedRows}
          getRowId={(row): string => row.id}
        />
      </div>
    );
  }
);

BufferTable.displayName = 'BufferTable';

export default BufferTable;
